import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheckSquare, FiUsers, FiTarget, FiClock, FiBell } from 'react-icons/fi';
import WatchlistPanel from '@/components/dashboards/WatchlistPanel';
import { useWorkspace } from '@/context/WorkspaceContext';

const DashboardPage: React.FC = () => {
  const { buildUrl, workspaceId } = useWorkspace();

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-mono font-semibold text-cm-text-primary">Dashboard</h1>
        <p className="mt-1 text-sm text-cm-text-secondary">
          Your overview — notifications, schedule, and pending actions.
        </p>
      </div>

      {/* Quick nav cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to={buildUrl({ kind: 'work' })}
          className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-5 hover:border-cm-accent-orange transition-colors group"
        >
          <FiCheckSquare className="w-6 h-6 text-cm-accent-blue mb-3" />
          <h3 className="font-mono font-medium text-cm-text-primary group-hover:text-cm-accent-orange transition-colors">Your Work</h3>
          <p className="text-sm text-cm-text-muted mt-1">Tasks, queue, and personal forecast</p>
        </Link>
        <Link
          to={buildUrl({ kind: 'teams' })}
          className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-5 hover:border-cm-accent-blue transition-colors group"
        >
          <FiUsers className="w-6 h-6 text-cm-accent-green mb-3" />
          <h3 className="font-mono font-medium text-cm-text-primary group-hover:text-cm-accent-blue transition-colors">Your Teams</h3>
          <p className="text-sm text-cm-text-muted mt-1">Team overview, forecast, and deadlines</p>
        </Link>
        <Link
          to={buildUrl({ kind: 'interests' })}
          className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-5 hover:border-cm-accent-purple transition-colors group"
        >
          <FiTarget className="w-6 h-6 text-cm-accent-purple mb-3" />
          <h3 className="font-mono font-medium text-cm-text-primary group-hover:text-cm-accent-purple transition-colors">Your Interests</h3>
          <p className="text-sm text-cm-text-muted mt-1">Stakeholder priorities and deliveries</p>
        </Link>
      </div>

      {/* Notifications */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiBell className="w-5 h-5 text-cm-accent-blue" />
          <h2 className="font-mono font-medium text-cm-text-primary">Notifications</h2>
          <span className="text-xs font-mono text-cm-text-muted ml-auto">0 unread</span>
        </div>
        <div className="space-y-2">
          <p className="text-sm text-cm-text-muted text-center py-4">No notifications yet.</p>
        </div>
      </div>

      <WatchlistPanel workspaceId={workspaceId} />

      {/* Schedule overview placeholder */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <h2 className="font-mono font-medium text-cm-text-primary mb-4">Schedule Overview</h2>
        <div className="flex items-center justify-center h-48 border border-dashed border-cm-border-mid rounded-lg">
          <p className="text-cm-text-muted text-sm font-mono">FuzzyGanttChart will be embedded here</p>
        </div>
      </div>

      {/* Pending actions placeholder */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiClock className="w-5 h-5 text-cm-accent-gold" />
          <h2 className="font-mono font-medium text-cm-text-primary">Pending Actions</h2>
        </div>
        <p className="text-sm text-cm-text-muted">No pending actions right now.</p>
      </div>
    </div>
  );
};

export default DashboardPage;
